// frontend/src/components/planner/SubscriptionsPanel.jsx
// Dedicated page for AI-detected subscription suggestions with matching

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  RefreshCw,
  Check,
  X,
  Calendar,
  DollarSign,
  AlertCircle,
  Building2,
  Clock,
  ArrowLeft,
  Zap,
  Link2,
  ChevronDown,
  Plus,
  Edit2
} from 'lucide-react';
import api from '../../utils/api';
import AppShell from '../layout/AppShell';
import LoadingSpinner from '../common/LoadingSpinner';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

// Enhanced "Add as New" editor with name, amount, type, and next occurrence
function AddAsNewEditor({ suggestion, addAsNewEdit, setAddAsNewEdit, editingAmount, setEditingAmount, selectedTypes, setSelectedTypes, processingId, handleAcceptAsNew, formatNextOccurrence }) {
  const amount = parseFloat(suggestion.typical_amount || 0);
  const suggestedType = suggestion.suggested_item_type || 'bill';
  const currentType = selectedTypes[suggestion.id] || suggestedType;
  const nextDate = formatNextOccurrence(suggestion);
  const displayAmount = editingAmount?.suggestionId === suggestion.id
    ? editingAmount.amount
    : amount.toFixed(2);

  return (
    <div className="w-full px-4 py-3 bg-green-50 border border-green-200 rounded-lg space-y-3">
      {/* Name */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">Name</label>
        <input
          type="text"
          value={addAsNewEdit.name}
          onChange={(e) => setAddAsNewEdit({ ...addAsNewEdit, name: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAcceptAsNew(suggestion.id); if (e.key === 'Escape') { setAddAsNewEdit(null); setEditingAmount(null); } }}
          autoFocus
          className="w-full px-2 py-1.5 text-base sm:text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Enter name..."
        />
      </div>

      {/* Amount + Type row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Amount (editable) */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Amount</label>
          {editingAmount?.suggestionId === suggestion.id ? (
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-gray-700">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={displayAmount}
                onChange={(e) => setEditingAmount({ ...editingAmount, amount: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAcceptAsNew(suggestion.id);
                  if (e.key === 'Escape') setEditingAmount(null);
                }}
                className="w-24 px-2 py-1 text-base sm:text-sm font-semibold border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          ) : (
            <button
              onClick={() => setEditingAmount({ suggestionId: suggestion.id, amount: amount.toFixed(2) })}
              className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-green-700"
              title="Edit amount"
            >
              ${amount.toFixed(2)}
              <Edit2 className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>

        {/* Type selector */}
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
          {suggestedType === 'income' ? (
            <span className="inline-block px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
              Income
            </span>
          ) : (
            <div className="flex gap-1">
              <button
                onClick={() => setSelectedTypes(prev => ({ ...prev, [suggestion.id]: 'bill' }))}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                  currentType === 'bill'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-gray-300 text-gray-500 hover:border-gray-400'
                }`}
              >
                Bill
              </button>
              <button
                onClick={() => setSelectedTypes(prev => ({ ...prev, [suggestion.id]: 'debt' }))}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                  currentType === 'debt'
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-gray-300 text-gray-500 hover:border-gray-400'
                }`}
              >
                Debt
              </button>
            </div>
          )}
        </div>

        {/* Next occurrence */}
        {nextDate && (
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Next</label>
            <span className="text-sm text-gray-700">{nextDate}</span>
          </div>
        )}
      </div>

      {/* Confirm / Cancel */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => handleAcceptAsNew(suggestion.id)}
          disabled={processingId === suggestion.id || !addAsNewEdit.name.trim()}
          className="px-4 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          {processingId === suggestion.id ? 'Adding...' : 'Confirm'}
        </button>
        <button
          onClick={() => { setAddAsNewEdit(null); setEditingAmount(null); }}
          className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 min-h-[44px] sm:min-h-0"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function SubscriptionsPanel() {
  const navigate = useNavigate();
  const { activeFilter, entities } = useBusinessEntity();
  const [suggestions, setSuggestions] = useState([]);
  const [recurringItems, setRecurringItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanPhase, setScanPhase] = useState(''); // 'detecting' | 'matching' | 'done'
  const [scanProgress, setScanProgress] = useState(0); // 0-100
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  // Pending link selection: { suggestionId, recurringItem }
  const [pendingLink, setPendingLink] = useState(null);
  // "Add as New" name editing: { suggestionId, name }
  const [addAsNewEdit, setAddAsNewEdit] = useState(null);
  // Expanded transactions view: { suggestionId: transactions[] }
  const [expandedTransactions, setExpandedTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState(null);
  // Type selection for "Add as New": { suggestionId: 'bill' | 'debt' | 'income' }
  const [selectedTypes, setSelectedTypes] = useState({});
  // Amount editing for "Add as New": { suggestionId, amount }
  const [editingAmount, setEditingAmount] = useState(null);

  // Build bank_account_id → business_entity_id map from context entities
  const businessAccountMap = useMemo(() => {
    const map = {};
    (entities || []).forEach(e => {
      (e.linkedAccountIds || []).forEach(id => { map[id] = e.id; });
    });
    return map;
  }, [entities]);

  // Filter suggestions by active entity filter
  const filteredSuggestions = useMemo(() => {
    if (activeFilter === 'all') return suggestions;
    return suggestions.filter(s => {
      const entityId = businessAccountMap[s.bank_account_id];
      if (activeFilter === 'personal') return !entityId;
      return entityId === activeFilter;
    });
  }, [suggestions, activeFilter, businessAccountMap]);

  // Filter recurring items (dropdown targets) by the suggestion's entity context
  // Even in "All" view, a personal suggestion should only show personal recurring items
  const getRecurringItemsForSuggestion = (suggestion) => {
    const suggestionEntityId = businessAccountMap[suggestion.bank_account_id];
    return recurringItems.filter(item => {
      const itemEntityId = businessAccountMap[item.bank_account_id];
      if (suggestionEntityId) {
        // Business suggestion → only show items from same business entity
        return itemEntityId === suggestionEntityId;
      } else {
        // Personal suggestion → only show personal items
        return !itemEntityId;
      }
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Initialize selectedTypes when suggestions load
  useEffect(() => {
    if (suggestions.length > 0) {
      const initialTypes = {};
      suggestions.forEach(s => {
        initialTypes[s.id] = s.suggested_item_type || 'bill';
      });
      setSelectedTypes(initialTypes);
    }
  }, [suggestions]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      
      // Load cached data WITHOUT triggering AI analysis
      const [suggestionsRes, itemsRes] = await Promise.all([
        api.get('/api/planner/recurring-suggestions?runAIMatch=false'),
        api.get('/api/planner/recurring-items-for-matching')
      ]);
      
      if (suggestionsRes.data.success) {
        setSuggestions(suggestionsRes.data.suggestions);
      }
      if (itemsRes.data.success) {
        setRecurringItems(itemsRes.data.items);
      }
    } catch (err) {
      setError('Failed to load suggestions');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleRescan() {
    try {
      setScanning(true);
      setError(null);
      setSuccessMessage(null);
      setSuggestions([]);
      setScanPhase('detecting');
      setScanProgress(15);

      // Step 1: Detect new patterns from bank transactions
      await api.post('/api/planner/detect-recurring');
      setScanProgress(45);
      setScanPhase('matching');

      // Step 2: Load suggestions WITH AI matching in one pass
      setScanProgress(60);
      const [suggestionsRes, itemsRes] = await Promise.all([
        api.get('/api/planner/recurring-suggestions?runAIMatch=true'),
        api.get('/api/planner/recurring-items-for-matching')
      ]);
      setScanProgress(90);

      if (suggestionsRes.data.success) {
        setSuggestions(suggestionsRes.data.suggestions);
      }
      if (itemsRes.data.success) {
        setRecurringItems(itemsRes.data.items);
      }

      setScanProgress(100);
      setScanPhase('done');
      setSuccessMessage('Scan complete! Review your detected subscriptions below.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError('Failed to scan for subscriptions');
      console.error('Error detecting recurring:', err);
    } finally {
      setScanning(false);
      setScanProgress(0);
      setScanPhase('');
    }
  }

  // Select an item from dropdown - shows confirmation
  function handleSelectLinkItem(suggestionId, recurringItem) {
    setExpandedDropdown(null);
    setPendingLink({ suggestionId, recurringItem });
  }

  // Cancel the pending link
  function handleCancelLink() {
    setPendingLink(null);
  }

  // Confirm the pending link
  async function handleConfirmPendingLink() {
    if (!pendingLink) return;
    await handleConfirmMatch(pendingLink.suggestionId, pendingLink.recurringItem.id);
    setPendingLink(null);
  }

  async function handleConfirmMatch(suggestionId, recurringItemId) {
    try {
      setProcessingId(suggestionId);
      setExpandedDropdown(null);
      setPendingLink(null);
      
      const res = await api.post(`/api/planner/recurring-suggestions/${suggestionId}/confirm-match`, {
        recurringItemId,
        updateAmount: true
      });
      
      if (res.data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        setSuccessMessage('Linked successfully! Future transactions will auto-match.');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      setError('Failed to link subscription');
      console.error('Error confirming match:', err);
    } finally {
      setProcessingId(null);
    }
  }

  async function handleAcceptAsNew(suggestionId) {
    // If not already editing, show the editor first
    if (!addAsNewEdit || addAsNewEdit.suggestionId !== suggestionId) {
      const suggestion = suggestions.find(s => s.id === suggestionId);
      const defaultName = suggestion?.normalized_name || suggestion?.merchant_name || '';
      setAddAsNewEdit({ suggestionId, name: defaultName });
      setExpandedDropdown(null);
      setPendingLink(null);
      return;
    }

    // Already editing — submit with edited fields
    const editedName = addAsNewEdit.name.trim();
    if (!editedName) return;

    const suggestion = suggestions.find(s => s.id === suggestionId);
    const itemType = selectedTypes[suggestionId] || suggestion?.suggested_item_type || 'bill';
    const amount = editingAmount?.suggestionId === suggestionId
      ? parseFloat(editingAmount.amount)
      : parseFloat(suggestion?.typical_amount || 0);

    try {
      setProcessingId(suggestionId);
      const res = await api.post(`/api/planner/recurring-suggestions/${suggestionId}/accept`, {
        name: editedName,
        item_type: itemType,
        amount: amount
      });

      if (res.data.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        setAddAsNewEdit(null);
        setEditingAmount(null);
        setSuccessMessage(`Added "${editedName}" as new recurring item!`);
        setTimeout(() => setSuccessMessage(null), 3000);

        // Refresh the recurring items list so the new item appears in dropdowns
        const itemsRes = await api.get('/api/planner/recurring-items-for-matching');
        if (itemsRes.data.success) {
          setRecurringItems(itemsRes.data.items);
        }
      }
    } catch (err) {
      setError('Failed to add subscription');
      console.error('Error accepting suggestion:', err);
    } finally {
      setProcessingId(null);
    }
  }

  async function handleDismiss(suggestionId) {
    try {
      setProcessingId(suggestionId);
      await api.post(`/api/planner/recurring-suggestions/${suggestionId}/dismiss`);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    } catch (err) {
      setError('Failed to dismiss suggestion');
      console.error('Error dismissing suggestion:', err);
    } finally {
      setProcessingId(null);
    }
  }

  async function toggleTransactions(suggestionId) {
    // If already expanded, collapse
    if (expandedTransactions[suggestionId]) {
      setExpandedTransactions(prev => {
        const next = { ...prev };
        delete next[suggestionId];
        return next;
      });
      return;
    }
    
    // Load transactions
    try {
      setLoadingTransactions(suggestionId);
      const res = await api.get(`/api/planner/recurring-suggestions/${suggestionId}/transactions`);
      if (res.data.success) {
        setExpandedTransactions(prev => ({
          ...prev,
          [suggestionId]: res.data.transactions
        }));
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setLoadingTransactions(null);
    }
  }

  // Calculate next occurrence date from frequency + last occurrence
  function calculateNextOccurrence(frequency, dayOfWeek, dayOfMonth, lastOccurrence) {
    if (!frequency) return null;
    const today = new Date();
    const last = lastOccurrence ? new Date(lastOccurrence + 'T12:00:00') : today;
    let next = new Date(last);

    switch (frequency.toLowerCase()) {
      case 'weekly':
        next.setDate(next.getDate() + 7);
        while (next <= today) next.setDate(next.getDate() + 7);
        break;
      case 'biweekly':
        next.setDate(next.getDate() + 14);
        while (next <= today) next.setDate(next.getDate() + 14);
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + 1);
        if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        while (next <= today) {
          next.setMonth(next.getMonth() + 1);
          if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        break;
      case 'quarterly':
        next.setMonth(next.getMonth() + 3);
        if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        while (next <= today) {
          next.setMonth(next.getMonth() + 3);
          if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        break;
      case 'yearly':
        next.setFullYear(next.getFullYear() + 1);
        if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        while (next <= today) {
          next.setFullYear(next.getFullYear() + 1);
          if (dayOfMonth) next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        break;
      default:
        return null;
    }
    return next;
  }

  function formatNextOccurrence(suggestion) {
    const next = calculateNextOccurrence(
      suggestion.detected_frequency,
      suggestion.detected_day_of_week,
      suggestion.detected_day_of_month,
      suggestion.last_occurrence
    );
    if (!next) return null;
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${dayNames[next.getDay()]}, ${monthNames[next.getMonth()]} ${next.getDate()}`;
  }

  const formatAmount = (amount) => {
    const num = Math.abs(parseFloat(amount));
    if (isNaN(num)) return '$—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const formatFrequency = (frequency) => {
    const map = {
      'weekly': 'Weekly',
      'biweekly': 'Every 2 weeks',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'yearly': 'Yearly'
    };
    return map[frequency] || frequency || 'Unknown';
  };

  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'weekly':
      case 'biweekly':
        return <Clock className="w-4 h-4" />;
      case 'monthly':
        return <Calendar className="w-4 h-4" />;
      default:
        return <RefreshCw className="w-4 h-4" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    if (!confidence) return 'bg-gray-200';
    if (confidence >= 0.8) return 'bg-green-500';
    if (confidence >= 0.6) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" text="Loading suggestions..." />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900 flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-purple-600" />
              Detected Subscriptions
            </h1>
            <p className="text-base sm:text-sm text-gray-600 mt-1">
              AI-powered detection of recurring payments from your transaction history
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRescan}
              disabled={scanning}
              className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : 'Detect Bills'}
            </button>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 text-green-800">
            <Check className="w-5 h-5 text-green-600" />
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-800">
            <AlertCircle className="w-5 h-5 text-red-600" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-purple-900">How it works</h3>
              <p className="text-base sm:text-sm text-purple-700 mt-1">
                We analyze your bank transactions to find recurring patterns like subscriptions,
                memberships, and regular bills. Accept suggestions to add them to your planner,
                or dismiss ones you don't want to track.
              </p>
            </div>
          </div>
        </div>

        {/* Scanning Progress */}
        {scanning && (
          <div className="bg-white rounded-lg border border-gray-200 p-12">
            <div className="max-w-md mx-auto text-center">
              <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-6" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {scanPhase === 'detecting' && 'Analyzing transaction patterns...'}
                {scanPhase === 'matching' && 'Running AI matching...'}
                {scanPhase === 'done' && 'Finishing up...'}
                {!scanPhase && 'Starting scan...'}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {scanPhase === 'detecting'
                  ? 'Scanning your bank transactions for recurring payments'
                  : scanPhase === 'matching'
                  ? 'Matching detected patterns to your existing forecast items'
                  : 'Almost there...'}
              </p>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-purple-600 h-2.5 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${scanProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{scanProgress}%</p>
            </div>
          </div>
        )}

        {/* Suggestions List */}
        {!scanning && filteredSuggestions.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {suggestions.length > 0 ? 'No subscriptions for this filter' : 'No subscriptions detected'}
            </h3>
            <p className="text-base sm:text-sm text-gray-600 mb-6 max-w-md mx-auto">
              {suggestions.length > 0
                ? 'Try selecting a different entity filter to see more results.'
                : 'We haven\'t found any recurring payments yet. Try scanning again after more transactions have synced from your bank.'
              }
            </p>
            {suggestions.length === 0 && (
              <button
                onClick={handleRescan}
                disabled={scanning}
                className="inline-flex items-center gap-2 px-6 py-3 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${scanning ? 'animate-spin' : ''}`} />
                Detect Bills
              </button>
            )}
          </div>
        ) : !scanning ? (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-200 rounded-t-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                <span className="font-semibold text-gray-900">
                  {filteredSuggestions.length} subscription{filteredSuggestions.length !== 1 ? 's' : ''} found
                  {activeFilter !== 'all' && suggestions.length !== filteredSuggestions.length && (
                    <span className="text-gray-400 font-normal text-base sm:text-sm ml-2">({suggestions.length} total)</span>
                  )}
                </span>
                <span className="text-base sm:text-sm text-gray-500">
                  Review and link to your existing bills, or add as new
                </span>
              </div>
            </div>
            
            <div className="divide-y divide-gray-100">
              {filteredSuggestions.map((suggestion) => {
                const hasAIMatch = suggestion.suggestedRecurringItem;
                const confidence = suggestion.ai_match_confidence;
                
                return (
                  <div
                    key={suggestion.id}
                    className={`p-4 sm:p-6 transition-colors ${hasAIMatch ? 'bg-purple-50/30' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex flex-col gap-4">
                      {/* Main Info Row */}
                      <div className="flex items-start justify-between gap-3 sm:gap-6">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-gray-900 text-lg">
                              {suggestion.merchant_name}
                            </h4>
                            {suggestion.suggested_item_type === 'income' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                Income
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 mt-3 text-base sm:text-sm text-gray-600">
                            <span className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{formatAmount(suggestion.typical_amount)}</span>
                            </span>
                            <span className="flex items-center gap-2">
                              {getFrequencyIcon(suggestion.detected_frequency)}
                              {formatFrequency(suggestion.detected_frequency)}
                            </span>
                            <button
                              onClick={() => toggleTransactions(suggestion.id)}
                              className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              <Calendar className="w-4 h-4" />
                              {loadingTransactions === suggestion.id ? (
                                <span className="flex items-center gap-1">
                                  <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" />
                                  Loading...
                                </span>
                              ) : (
                                <span>
                                  {suggestion.occurrence_count} transactions
                                  <ChevronDown className={`w-3 h-3 inline ml-1 transition-transform ${expandedTransactions[suggestion.id] ? 'rotate-180' : ''}`} />
                                </span>
                              )}
                            </button>
                            {suggestion.account_name && (
                              <span className="flex items-center gap-2 text-blue-600">
                                <Building2 className="w-4 h-4" />
                                {suggestion.account_name}
                              </span>
                            )}
                          </div>
                          
                          {/* Expanded Transactions */}
                          {expandedTransactions[suggestion.id] && (
                            <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="text-base sm:text-xs font-medium text-gray-500 mb-2">Sample Transactions</div>
                              <div className="space-y-2">
                                {expandedTransactions[suggestion.id].map(txn => (
                                  <div key={txn.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-base sm:text-sm gap-1 sm:gap-3">
                                    <div className="flex items-center gap-3">
                                      <span className="text-gray-500 w-20 flex-shrink-0">{new Date(txn.date).toLocaleDateString()}</span>
                                      <span className="text-gray-700 truncate max-w-[200px]" title={txn.name}>
                                        {txn.merchant_name || txn.name}
                                      </span>
                                    </div>
                                    <span className={`font-medium ${txn.amount < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                                      {formatAmount(Math.abs(txn.amount))}
                                    </span>
                                  </div>
                                ))}
                                {expandedTransactions[suggestion.id].length === 0 && (
                                  <div className="text-base sm:text-sm text-gray-500 italic">No transaction details available</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* AI Match Section */}
                      {hasAIMatch ? (
                        <div className="bg-white rounded-lg border border-purple-200 p-3 sm:p-4">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Link2 className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-base sm:text-sm font-medium text-purple-700">AI Match Found</span>
                                <div className={`w-2 h-2 rounded-full ${getConfidenceColor(confidence)}`} />
                                <span className="text-base sm:text-xs text-gray-500">
                                  {Math.round((confidence || 0) * 100)}% confident
                                </span>
                              </div>
                              <p className="text-gray-900 font-medium">
                                Looks like "{suggestion.suggestedRecurringItem.name}"
                                <span className="text-gray-500 font-normal"> ({formatAmount(suggestion.suggestedRecurringItem.amount)})</span>
                              </p>
                              {suggestion.ai_match_reason && (
                                <p className="text-base sm:text-sm text-gray-600 mt-1">{suggestion.ai_match_reason}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons for AI Match */}
                          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-purple-100">
                            <button
                              onClick={() => handleConfirmMatch(suggestion.id, suggestion.suggestedRecurringItem.id)}
                              disabled={processingId === suggestion.id}
                              className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                            >
                              {processingId === suggestion.id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                              Yes, Link to "{suggestion.suggestedRecurringItem.name}"
                            </button>
                            
                            {/* Pick Different Dropdown */}
                            <div className="relative">
                              <button
                                onClick={() => setExpandedDropdown(expandedDropdown === suggestion.id ? null : suggestion.id)}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform ${expandedDropdown === suggestion.id ? 'rotate-180' : ''}`} />
                                Pick Different
                              </button>
                              
                              {expandedDropdown === suggestion.id && (
                                <div className="absolute z-10 mt-1 w-full min-w-[200px] sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                                  {getRecurringItemsForSuggestion(suggestion).filter(item => item.id !== suggestion.suggestedRecurringItem?.id).map(item => (
                                    <button
                                      key={item.id}
                                      onClick={() => handleSelectLinkItem(suggestion.id, item)}
                                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                                    >
                                      <span className="font-medium text-gray-900">{item.name}</span>
                                      <span className="text-sm text-gray-500">{formatAmount(item.amount)}</span>
                                    </button>
                                  ))}
                                  {getRecurringItemsForSuggestion(suggestion).filter(item => item.id !== suggestion.suggestedRecurringItem?.id).length === 0 && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                      No other matching items
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Show pending confirmation for Pick Different */}
                            {pendingLink?.suggestionId === suggestion.id && (
                              <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                                <span className="text-sm text-purple-800">
                                  Link to <span className="font-semibold">"{pendingLink.recurringItem.name}"</span>?
                                </span>
                                <button
                                  onClick={handleConfirmPendingLink}
                                  disabled={processingId === suggestion.id}
                                  className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                                >
                                  {processingId === suggestion.id ? 'Linking...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={handleCancelLink}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                            
                            {/* Add as New — with enhanced editor */}
                            {addAsNewEdit?.suggestionId === suggestion.id ? (
                              <AddAsNewEditor
                                suggestion={suggestion}
                                addAsNewEdit={addAsNewEdit}
                                setAddAsNewEdit={setAddAsNewEdit}
                                editingAmount={editingAmount}
                                setEditingAmount={setEditingAmount}
                                selectedTypes={selectedTypes}
                                setSelectedTypes={setSelectedTypes}
                                processingId={processingId}
                                handleAcceptAsNew={handleAcceptAsNew}
                                formatNextOccurrence={formatNextOccurrence}
                              />
                            ) : (
                              <button
                                onClick={() => handleAcceptAsNew(suggestion.id)}
                                disabled={processingId === suggestion.id}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                                Add as New
                              </button>
                            )}

                            <button
                              onClick={() => handleDismiss(suggestion.id)}
                              disabled={processingId === suggestion.id}
                              className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Dismiss
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* No AI Match - Simple Add/Link Options */
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Add as New — with enhanced editor */}
                          {addAsNewEdit?.suggestionId === suggestion.id ? (
                            <AddAsNewEditor
                              suggestion={suggestion}
                              addAsNewEdit={addAsNewEdit}
                              setAddAsNewEdit={setAddAsNewEdit}
                              editingAmount={editingAmount}
                              setEditingAmount={setEditingAmount}
                              selectedTypes={selectedTypes}
                              setSelectedTypes={setSelectedTypes}
                              processingId={processingId}
                              handleAcceptAsNew={handleAcceptAsNew}
                              formatNextOccurrence={formatNextOccurrence}
                            />
                          ) : (
                            <button
                              onClick={() => handleAcceptAsNew(suggestion.id)}
                              disabled={processingId === suggestion.id}
                              className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              {processingId === suggestion.id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                              Add as New
                            </button>
                          )}
                          
                          {/* Link to existing dropdown or pending confirmation */}
                          {pendingLink?.suggestionId === suggestion.id ? (
                            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                              <span className="text-sm text-purple-800">
                                Link to <span className="font-semibold">"{pendingLink.recurringItem.name}"</span>?
                              </span>
                              <button
                                onClick={handleConfirmPendingLink}
                                disabled={processingId === suggestion.id}
                                className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                              >
                                {processingId === suggestion.id ? 'Linking...' : 'Confirm'}
                              </button>
                              <button
                                onClick={handleCancelLink}
                                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() => setExpandedDropdown(expandedDropdown === `link-${suggestion.id}` ? null : `link-${suggestion.id}`)}
                                className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                              >
                                <Link2 className="w-4 h-4" />
                                Link to Existing
                                <ChevronDown className={`w-4 h-4 transition-transform ${expandedDropdown === `link-${suggestion.id}` ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {expandedDropdown === `link-${suggestion.id}` && (
                                <div className="absolute z-10 mt-1 w-full min-w-[200px] sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                                  {getRecurringItemsForSuggestion(suggestion).map(item => (
                                    <button
                                      key={item.id}
                                      onClick={() => handleSelectLinkItem(suggestion.id, item)}
                                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                                    >
                                      <span className="font-medium text-gray-900">{item.name}</span>
                                      <span className="text-sm text-gray-500">{formatAmount(item.amount)}</span>
                                    </button>
                                  ))}
                                  {getRecurringItemsForSuggestion(suggestion).length === 0 && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                      No matching items
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <button
                            onClick={() => handleDismiss(suggestion.id)}
                            disabled={processingId === suggestion.id}
                            className="flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Quick Action */}
        <div className="flex justify-center">
          <button
            onClick={() => navigate('/planner/recurring')}
            className="text-purple-600 hover:text-purple-700 text-base sm:text-sm font-medium flex items-center gap-2 min-h-[44px] sm:min-h-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Bills & Income
          </button>
        </div>
      </div>
    </AppShell>
  );
}
